/* eslint-disable prefer-arrow-callback */
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { useLocation, useNavigate } from "utils";

import type { DroppedMainCrop } from "models";

import { type CropYear } from "pages/app/scenario-analysis/types/scenario";

/** How the page was entered from a field import: nothing to import, already saved, or waiting for a project choice. */
type ImportLandingMode = "none" | "persisted" | "awaitingProjectChoice";

type ImportRouterState = {
  importedCropYears?: CropYear[];
  selectedFieldId?: string;
  fieldImportAlreadyPersisted?: boolean;
  showFieldImportTransition?: boolean;
  /** Set when arriving from FCPO with existing projects — skip pre-persist and show dialog. */
  showProjectEntryDialog?: boolean;
  cropRotationLoaded?: boolean;
  importReadyForRecalc?: boolean;
  /** Crops the API couldn't transfer during field import — drives the unsupported-crops alert banner. */
  droppedMainCrops?: DroppedMainCrop[];
} | null;

const resolveImportLandingMode = (state: ImportRouterState): ImportLandingMode => {
  if (state?.showProjectEntryDialog) return "awaitingProjectChoice";
  if (state?.fieldImportAlreadyPersisted) return "persisted";
  return "none";
};

const hasImportRouterState = (state: ImportRouterState): boolean =>
  Boolean(state?.showFieldImportTransition) ||
  Boolean(state?.importedCropYears) ||
  Boolean(state?.fieldImportAlreadyPersisted) ||
  Boolean(state?.selectedFieldId);

/** The router state a field import lands on this page with: read once on mount, then cleared so a refresh does not replay it. */
export const useFieldImportLandingState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Frozen on mount: consumeImportRouterStateOnce clears location.state, so re-reading it would flip to "none".
  const [landingState] = useState(() => location.state as ImportRouterState);
  const [importLandingMode] = useState(() => resolveImportLandingMode(landingState));
  const isAwaitingProjectChoice = importLandingMode === "awaitingProjectChoice";
  // Imported years wait here until the user picks a project in the entry dialog ("awaitingProjectChoice" only).
  const importedFieldCropYearsRef = useRef<CropYear[] | null>(landingState?.importedCropYears ?? null);
  const importedSelectedFieldIdRef = useRef<string | null>(landingState?.selectedFieldId ?? null);
  // True once the imported years are saved (a "persisted" landing, or the entry dialog); the page clears it when the user leaves that project.
  const fieldImportAlreadyPersistedRef = useRef(importLandingMode === "persisted");
  // Re-runs the post-hydration auto-recalc once the entry dialog is dismissed without a choice.
  const [projectEntryDismissed, setProjectEntryDismissed] = useState(false);
  const dismissProjectEntry = useCallback(function dismissProjectEntry() {
    setProjectEntryDismissed(true);
  }, []);

  useLayoutEffect(function consumeImportRouterStateOnce() {
    if (!hasImportRouterState(location.state as ImportRouterState)) return;

    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume router state once on mount so refresh does not replay import UI
  }, []);

  const locationRef = useRef(location);
  locationRef.current = location;
  // The entry dialog writes import state into this history entry without a remount, so it is cleared once the landing is done.
  const clearImportRouterState = useCallback(
    function clearImportRouterState() {
      const { pathname, search, hash, state } = locationRef.current;
      if (!hasImportRouterState(state as ImportRouterState)) return;
      navigate({ pathname, search, hash }, { replace: true, state: null });
    },
    [navigate]
  );

  return {
    importLandingMode,
    clearImportRouterState,
    isAwaitingProjectChoice,
    importedFieldCropYearsRef,
    importedSelectedFieldIdRef,
    fieldImportAlreadyPersistedRef,
    projectEntryDismissed,
    dismissProjectEntry,
    // The values below seed page state on mount; the entry dialog flow drives its own once the user chooses.
    showTransitionOnLanding: Boolean(landingState?.showFieldImportTransition) && !isAwaitingProjectChoice,
    awaitingCalculationOnLanding: Boolean(landingState?.importReadyForRecalc) && !isAwaitingProjectChoice,
    transitionCropRotationLoaded: landingState?.cropRotationLoaded ?? true,
    transitionImportReadyForRecalc: Boolean(landingState?.importReadyForRecalc),
    droppedMainCropsOnLanding: landingState?.droppedMainCrops ?? [],
  };
};
